import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Button,
  useMediaQuery,
  useTheme,
  Modal,
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
  Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import { useEventContext, TrackEventCodes } from '../../context/EventContext';
import { isSpeedEvent } from '../../utils/eventUtils';

const EventOptions = ({
  heatDetails,
  totalHeats,
  unifiedStyles, // Prop para recibir estilos unificados
  open, // Para modo móvil
  onClose, // Para modo móvil
  mobileMode = false, // Para saber si está en modo móvil
  hiddenInMobile = false, // NUEVA PROP: Para ocultar en móvil
  // Props existentes
  editingResults,
  setEditingResults,
  editingByLane,
  setEditingByLane,
  windEnabled,
}) => {
  const { updateTestSettings, applyAdvancementFormula, getTestSettings, clearQualifiers } = useEventContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [openSettingsModal, setOpenSettingsModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const { eventNumber, round, heat, eventCode } = heatDetails;

  // Estado temporal de ajustes
  const [tempSettings, setTempSettings] = useState(() => getTestSettings(eventNumber, round));

  const tempSettingsRef = useRef(tempSettings);

  const code = eventCode;
  const isTrackEvent = TrackEventCodes.includes(code);
  
  // Separamos los tipos de eventos horizontales
  const jumpEvents = ['LJ1', 'TJ1'].includes(code);
  const throwEvents = ['DT1', 'HT1', 'SP1', 'JT1'].includes(code);
  const horizontalField = jumpEvents || throwEvents;
  
  const speedEvent = isTrackEvent && isSpeedEvent(code);

  // Asegurarse de que totalHeats sea un número válido
  const heatsCount = parseInt(totalHeats, 10) || 1;

  // Manejador de cambios
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
  
    // Si el usuario DES-marca "Tiene Final"
    if (name === 'hasFinalRound' && !checked) {
      setTempSettings(prev => ({
        ...prev,
        hasFinalRound: false,
        // limpia todos los campos relacionados con la final
        lanesCount: 0,
        advancementType: '',
        advancementDetail: '',
        numberOfPreliminaryAttempts: 0,
        athletesInFinal: 0,
      }));
      return;       // --> ya actualizamos, salimos
    }
  
    // Resto de campos
    setTempSettings(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'number'
          ? parseInt(value, 10) || 0
          : value,
    }));
  
    // Si cambia el tipo de avance, reinicia el detalle
    if (name === 'advancementType') {
      setTempSettings(prev => ({ ...prev, advancementDetail: '' }));
    }
  };

  /**
   * Genera opciones de avance según número de heats y carriles.
   */
  const computeAdvOptions = (heats, lanes) => {
    const options = [];
    if (heats >= lanes) {
      options.push({ top: 0, best: lanes });
      return options;
    }
    const maxTopPerHeat = Math.floor(lanes / heats);
    for (let topPerHeat = maxTopPerHeat; topPerHeat >= 0; topPerHeat--) {
      const positionSlots = topPerHeat * heats;
      const bestTimeSlots = lanes - positionSlots;
      options.push({ top: topPerHeat, best: bestTimeSlots });
    }
    return options;
  };

  const advOptions = useMemo(
    () => computeAdvOptions(heatsCount, tempSettings.lanesCount),
    [heatsCount, tempSettings.lanesCount]
  );

  // Guardar ajustes
  const handleSaveSettings = async () => {
    try {
      const {
        numberOfAttempts,
        numberOfPreliminaryAttempts,
        hasFinalRound,
        enableWind,
        enablePoints,
        lanesCount,
        advancementType,
        advancementDetail,
        athletesInFinal,
      } = tempSettings;

      const errors = [];

      // --- Validaciones comunes ---
      if (numberOfAttempts < 0 || numberOfAttempts > 6) {
        errors.push('Intentos deben estar entre 0 y 6');
      }
      if (numberOfPreliminaryAttempts > numberOfAttempts) {
        errors.push('Intentos preliminares no pueden superar el total');
      }

      // --- Validaciones PISTA ---
      if (hasFinalRound && isTrackEvent) {
        if (lanesCount < 2 || lanesCount > 10) {
          errors.push('Se requieren entre 2 y 10 carriles');
        }
        if (advancementType === 'posicion' && !advancementDetail) {
          errors.push('Debe seleccionar un formato de avance');
        }
      }

      // --- Validaciones CAMPO HORIZONTAL ---
      if (hasFinalRound && horizontalField) {
        // Atletas en final
        const maxParticipants = heatDetails.participants?.length || 0;
        if (athletesInFinal <= 0 || athletesInFinal > maxParticipants) {
          errors.push(`Atletas en final debe ser entre 1 y ${maxParticipants}`);
        }
      }
      // Si hubo errores, muéstralos y sal
      if (errors.length > 0) {
        setSnackbar({
          open: true,
          message: errors.join(' | '),
          severity: 'error',
        });
        return;
      }

      // Preparar datos para Firestore
      const settingsUpdate = {
        ...tempSettings,
        lastUpdated: new Date().toISOString(),
      };

      // SOLUCIÓN CLAVE: Manejo correcto de la secuencia de operaciones
      try {
        // Paso 1: Si está desactivando la final, primero limpiar calificadores
        if (!hasFinalRound) {
          await clearQualifiers(eventNumber, round);
        }
        
        // Paso 2: Actualizar configuraciones
        await updateTestSettings(eventNumber, round, heat, settingsUpdate);
        
        // Paso 3: Si tiene final, aplicar fórmula de avance con las configuraciones actualizadas
        if (hasFinalRound && advancementType === 'posicion' && advancementDetail) {
          // Usamos la configuración que acabamos de guardar, no dependemos del estado
          await applyAdvancementFormula(eventNumber, round, settingsUpdate);
        }
        
        setSnackbar({
          open: true,
          message: 'Configuración guardada exitosamente',
          severity: 'success',
        });
        
        // Cerrar modal después de 1s
        setTimeout(() => {
          if (mobileMode && onClose) {
            onClose();
          } else {
            setOpenSettingsModal(false);
          }
        }, 1000);
        
      } catch (error) {
        console.error('Error en secuencia de actualización:', error);
        setSnackbar({
          open: true,
          message: `Error al procesar cambios: ${error.message}`,
          severity: 'error',
        });
      }

    } catch (error) {
      console.error('Error al guardar configuración:', error);
      setSnackbar({
        open: true,
        message: `Error al guardar: ${error.message}`,
        severity: 'error',
      });
    }
  };

  const getModalWidth = () => {
    if (isMobile) return '90%';
    if (isTablet) return '450px';
    return '500px';
  };

  return (
    <>
      {/* Botón principal - solo mostrar si no está en modo móvil Y no está oculto */}
      {!mobileMode && !hiddenInMobile && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<SettingsIcon />}
          onClick={() => {
            setTempSettings(getTestSettings(eventNumber, round));
            setOpenSettingsModal(true);
          }}
          fullWidth={isMobile}
          sx={unifiedStyles}
        >
          Ajustes de Prueba
        </Button>
      )}

      {/* Modal con Fade */}
      <Modal 
        open={mobileMode ? (open || false) : openSettingsModal} 
        onClose={() => {
          if (mobileMode && onClose) {
            onClose();
          } else {
            setOpenSettingsModal(false);
          }
        }}
        closeAfterTransition
      >
        <Fade in={mobileMode ? (open || false) : openSettingsModal}>
          <Paper
            elevation={5}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: getModalWidth(),
              maxWidth: '95%',
              maxHeight: '90vh',
              overflowY: 'auto',
              bgcolor: 'background.paper',
              borderRadius: '12px',
              boxShadow: 24,
              p: 0,
            }}
          >
            {/* Cabecera del modal */}
            <Box 
              sx={{ 
                p: 2, 
                borderBottom: `1px solid ${theme.palette.divider}`,
                background: theme.palette.primary.main,
                color: 'white',
                borderRadius: '12px 12px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" fontWeight="600">
                Ajustes de la Prueba
              </Typography>
              <IconButton 
                onClick={() => {
                  if (mobileMode && onClose) {
                    onClose();
                  } else {
                    setOpenSettingsModal(false);
                  }
                }}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box sx={{ p: 3 }}>
              {/* Configuración básica */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom color="primary">
                  Configuración General
                </Typography>
                
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={tempSettings.enablePoints} 
                      onChange={handleChange} 
                      name="enablePoints"
                      color="primary"
                    />
                  }
                  label="Agregar Puntaje"
                  sx={{ mb: 1, width: '100%' }}
                />
                
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={tempSettings.hasFinalRound} 
                      onChange={handleChange} 
                      name="hasFinalRound"
                      color="primary"
                    />
                  }
                  label="Tiene Final"
                  sx={{ mb: 1, width: '100%' }}
                />
                
                {/* Viento - solo para eventos de salto y carreras de velocidad */}
                {(jumpEvents || speedEvent) && (
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={tempSettings.enableWind} 
                        onChange={handleChange} 
                        name="enableWind"
                        color="primary"
                      />
                    }
                    label="Habilitar Medición de Viento"
                    sx={{ mb: 1, width: '100%' }}
                  />
                )}
              </Box>
              
              {/* Intentos para eventos que no son de pista */}
              {!isTrackEvent && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom color="primary">
                    Configuración de Intentos
                  </Typography>
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Intentos Totales"
                    name="numberOfAttempts"
                    value={tempSettings.numberOfAttempts}
                    onChange={handleChange}
                    inputProps={{ min: 0, max: 6 }}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  
                  {/* Para eventos de campo horizontal con final */}
                  {tempSettings.hasFinalRound && horizontalField && (
                    <>
                      <TextField
                        fullWidth
                        type="number"
                        label="Intentos en Ronda Preliminar"
                        name="numberOfPreliminaryAttempts"
                        value={tempSettings.numberOfPreliminaryAttempts}
                        onChange={handleChange}
                        size="small"
                        variant="outlined"
                        sx={{ mb: 2 }}
                        inputProps={{ min: 0, max: tempSettings.numberOfAttempts }}
                      />
                      
                      <TextField
                        fullWidth
                        type="number"
                        label="Atletas en Final"
                        name="athletesInFinal"
                        value={tempSettings.athletesInFinal}
                        onChange={handleChange}
                        size="small"
                        variant="outlined"
                        sx={{ mb: 2 }}
                        inputProps={{ min: 1, max: heatDetails.participants?.length || 0 }}
                      />
                    </>
                  )}
                </Box>
              )}
              
              {/* Configuración de pista para eventos de pista con final */}
              {isTrackEvent && tempSettings.hasFinalRound && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom color="primary">
                    Configuración de Final
                  </Typography>
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Número de Carriles"
                    name="lanesCount"
                    value={tempSettings.lanesCount}
                    onChange={handleChange}
                    inputProps={{ min: 2, max: 10 }}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControl fullWidth sx={{ mb: 2 }} size="small">
                    <InputLabel id="adv-type-label">Tipo de Avance</InputLabel>
                    <Select
                      labelId="adv-type-label"
                      name="advancementType"
                      value={tempSettings.advancementType}
                      label="Tipo de Avance"
                      onChange={handleChange}
                    >
                      <MenuItem value="posicion">Por Posición</MenuItem>
                      <MenuItem value="tiempo">Por Tiempo</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {tempSettings.advancementType === 'posicion' && (
                    <FormControl fullWidth sx={{ mb: 2 }} size="small">
                      <InputLabel id="adv-detail-label">Formato Avance</InputLabel>
                      <Select
                        labelId="adv-detail-label"
                        name="advancementDetail"
                        value={tempSettings.advancementDetail}
                        label="Formato Avance"
                        onChange={handleChange}
                      >
                        {advOptions.map(opt => (
                          <MenuItem key={`${opt.top}-${opt.best}`} value={`${opt.top}-${opt.best}`}>
                            Top {opt.top} + {opt.best} Mejores tiempos
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}
            </Box>

            {/* Botones de acción */}
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, p: 3 }}>
              <Button 
                variant="outlined" 
                onClick={() => setOpenSettingsModal(false)}
                sx={{ 
                  borderRadius: '8px', 
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Cancelar
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSaveSettings}
                startIcon={<SaveIcon />}
                color="primary"
                sx={{ 
                  borderRadius: '8px', 
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Guardar
              </Button>
            </Box>
          </Paper>
        </Fade>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EventOptions;